import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '@ui/components/Button';
import { TextField } from '@ui/components/Field';
import { LoadingState } from '@ui/components/States';
import { DEFAULT_ROUTE } from '@app/nav/navigation';
import { isRecoveryHashRoute, useAuth, clearAuthErrorHash } from '@platform/auth/authContext';
import {
  validateEmailAddress,
  validateLoginForm,
  validatePasswordReset,
  validateSignUpForm,
} from '@platform/auth/validation';

function AuthCard({ title, subtitle, children }: { readonly title: string; readonly subtitle?: string; readonly children: ReactNode }) {
  return (
    <div className='flex min-h-screen items-center justify-center bg-slate-950 p-4 text-slate-100'>
      <div className='w-full max-w-md rounded-xl border border-slate-800 bg-slate-900/80 p-6 shadow-2xl shadow-slate-950/60'>
        <div className='mb-6'>
          <div className='mb-3 flex items-center justify-between gap-2'>
            <a href='#/' className='flex items-center gap-2 hover:opacity-90'>
              <img src='/assets/logo.svg' alt='' className='h-9 w-9 rounded-full' />
              <span className='text-sm font-semibold text-slate-100'>DNR</span>
            </a>
            <a href='#/' className='text-xs font-medium text-slate-400 transition-colors hover:text-slate-100 hover:underline'>
              ← Back to home
            </a>
          </div>
          <h1 className='mt-2 text-2xl font-semibold text-white'>{title}</h1>
          {subtitle ? <p className='mt-2 text-sm text-slate-400'>{subtitle}</p> : null}
        </div>
        {children}
      </div>
    </div>
  );
}
function normalizeAuthRoute(route: string): string {
  return route.split('?')[0] || '#/login';
}

export function AuthGate({ route }: { readonly route: string }) {
  const { state } = useAuth();
  const normalizedRoute = normalizeAuthRoute(route || '#/login');
  const recoveryRoute = isRecoveryHashRoute(route || '#/login');

  useEffect(() => {
    if (state === 'recovering' && normalizedRoute !== '#/reset-password') {
      window.location.hash = '#/reset-password';
      return;
    }

    if (state === 'unauthenticated' && !recoveryRoute && !['#/login', '#/signup', '#/forgot-password', '#/reset-password'].includes(normalizedRoute)) {
      window.location.hash = '#/login';
    }
  }, [normalizedRoute, recoveryRoute, state]);

  if (state === 'loading' && !isRecoveryHashRoute(route || '#/login')) {
    return <LoadingState label="Restoring your session..." className="justify-center min-h-screen" />;
  }

  if (state === 'recovering' || normalizedRoute === '#/reset-password' || isRecoveryHashRoute(route || '#/login')) {
    return <ResetPasswordScreen />;
  }

  switch (normalizedRoute) {
    case '#/login':
      return <LoginScreen />;
    case '#/signup':
      return <SignUpScreen />;
    case '#/forgot-password':
      return <ForgotPasswordScreen />;
    case '#/reset-password':
      return <ResetPasswordScreen />;
    default:
      return <LoginScreen />;
  }
}
export function LoginScreen() {
  const { signIn, error, clearError, justConfirmed } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  useEffect(() => {
    if (justConfirmed) {
      clearAuthErrorHash();
    }
  }, [justConfirmed]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    clearError();
    const localError = validateLoginForm(email, password);
    setFieldErrors({
      email: email.trim() ? undefined : 'Email is required.',
      password: password ? undefined : 'Password is required.',
    });
    if (localError) {
      return;
    }

    try {
      setIsSubmitting(true);
      await signIn(email.trim(), password);
      window.location.hash = DEFAULT_ROUTE;
    } catch {
      // error is surfaced via auth context.
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthCard title='Welcome back' subtitle='Sign in to continue managing your work.'>
      <form onSubmit={handleSubmit} className='space-y-4'>
        {justConfirmed ? (
          <div role='status' className='rounded border border-emerald-600/40 bg-emerald-950/40 p-3 text-sm text-emerald-200'>
            Email confirmed successfully. Please sign in.
          </div>
        ) : null}
        <TextField
          label='Email'
          type='email'
          autoComplete='email'
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder='you@example.com'
          error={fieldErrors.email ?? error ?? undefined}
        />
        <TextField
          label='Password'
          type='password'
          autoComplete='current-password'
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder='……………………'
          error={fieldErrors.password ?? error ?? undefined}
        />
        <Button type='submit' variant='primary' block loading={isSubmitting} loadingText='Signing in…'>
          Sign In
        </Button>
        <div className='flex flex-col gap-2 pt-2 text-sm text-slate-300'>
          <button type='button' className='text-left text-brand-400 hover:underline' onClick={() => { clearError(); window.location.hash = '#/forgot-password'; }}>
            Forgot password?
          </button>
          <button type='button' className='text-left text-brand-400 hover:underline' onClick={() => { clearError(); window.location.hash = '#/signup'; }}>
            Create account
          </button>
        </div>
      </form>
    </AuthCard>
  );
}
export function SignUpScreen() {
  const { signUp, error, clearError } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ fullName?: string; email?: string; password?: string; confirmPassword?: string }>({});

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    clearError();
    setSuccessMessage(null);
    const validationError = validateSignUpForm(email, password, confirmPassword);
    setFieldErrors({
      fullName: fullName.trim() ? undefined : 'Full name is required.',
      email: email.trim() ? undefined : 'Email is required.',
      password: password.length >= 8 ? undefined : 'Password must be at least 8 characters.',
      confirmPassword: confirmPassword ? (password === confirmPassword ? undefined : 'Passwords do not match.') : 'Please confirm your password.',
    });
    if (validationError || !fullName.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await signUp(email.trim(), password, fullName.trim());
      if (result.requiresEmailConfirmation) {
        setSuccessMessage('Please check your email and verify your account before signing in.');
        setFullName('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
      } else {
        window.location.hash = '#/login';
      }
    } catch {
      // surfaced via auth context.
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthCard title='Create DNR account'>
      <form onSubmit={handleSubmit} className='space-y-4'>
        <TextField label='Full Name' type='text' autoComplete='name' value={fullName} onChange={(event) => setFullName(event.target.value)} placeholder='John Doe' error={fieldErrors.fullName} />
        <TextField label='Email Address' type='email' autoComplete='email' value={email} onChange={(event) => setEmail(event.target.value)} placeholder='you@example.com' error={fieldErrors.email ?? error ?? undefined} />
        <TextField label='Password' type='password' autoComplete='new-password' value={password} onChange={(event) => setPassword(event.target.value)} placeholder='Minimum 8 characters' error={fieldErrors.password} />
        <TextField label='Confirm Password' type='password' autoComplete='new-password' value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder='Repeat your password' error={fieldErrors.confirmPassword} />
        {successMessage ? <div role='status' className='rounded border border-emerald-600/40 bg-emerald-950/40 p-3 text-sm text-emerald-200'>{successMessage}</div> : null}
        <Button type="submit" variant="primary" block loading={isSubmitting} loadingText="Creating account...">
          Create Account
        </Button>
        <div className='flex flex-col gap-2 pt-2 text-sm text-slate-300'>
          <button type='button' className='text-left text-brand-400 hover:underline' onClick={() => { clearError(); window.location.hash = '#/login'; }}>
            Already have an account? Sign in
          </button>
        </div>
      </form>
    </AuthCard>
  );
}
export function ForgotPasswordScreen() {
  const { requestPasswordReset, error, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    clearError();
    setLocalError(null);
    const e = validateEmailAddress(email);
    if (e) {
      setLocalError(e);
      return;
    }

    setIsSubmitting(true);
    requestPasswordReset(email.trim())
      .then(() => setSubmitted(true))
      .catch(() => {})
      .finally(() => setIsSubmitting(false));
  }

  return (
    <AuthCard title='Forgot password' subtitle='Enter your email to receive a reset link.'>
      <form onSubmit={handleSubmit} className='space-y-4'>
        {submitted ? (
          <div role='status' className='rounded border border-emerald-600/40 bg-emerald-950/40 p-3 text-sm text-emerald-200'>
            If an account exists for that email, we sent a password reset link.
          </div>
        ) : null}
        <TextField label='Email' type='email' autoComplete='email' value={email} onChange={(event) => setEmail(event.target.value)} placeholder='you@example.com' error={localError ?? error ?? undefined} />
        <Button type='submit' variant='primary' block loading={isSubmitting} loadingText='Sending…'>
          Send reset link
        </Button>
        <button type='button' className='text-sm text-brand-400 hover:underline' onClick={() => { clearError(); window.location.hash = '#/login'; }}>
          Back to sign in
        </button>
      </form>
    </AuthCard>
  );
}
export function ResetPasswordScreen() {
  const { confirmPasswordReset, state, error, clearError } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    clearError();
    setLocalError(null);
    const e = validatePasswordReset(password, confirmPassword);
    if (e) {
      setLocalError(e);
      return;
    }

    setIsSubmitting(true);
    try {
      await confirmPasswordReset(password);
      setSubmitted(true);
      setTimeout(() => {
        window.location.hash = '#/login';
      }, 1200);
    } catch {
      // error surfaced via context.
    } finally {
      setIsSubmitting(false);
    }
  }

  if (state === 'loading') {
    return <LoadingState label='Verifying reset link…' className='justify-center min-h-screen' />;
  }

  return (
    <AuthCard title='Reset password' subtitle='Choose a new password.'>
      <form onSubmit={handleSubmit} className='space-y-4'>
        {submitted ? (
          <div role='status' className='rounded border border-emerald-600/40 bg-emerald-950/40 p-3 text-sm text-emerald-200'>
            Password updated. Redirecting to sign in…
          </div>
        ) : null}
        <TextField label='New password' type='password' autoComplete='new-password' value={password} onChange={(event) => setPassword(event.target.value)} placeholder='Minimum 8 characters' error={localError ?? error ?? undefined} />
        <TextField label='Confirm new password' type='password' autoComplete='new-password' value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder='Repeat new password' error={localError ?? error ?? undefined} />
        <Button type='submit' variant='primary' block loading={isSubmitting} loadingText='Updating…'>
          Update password
        </Button>
      </form>
    </AuthCard>
  );
}